import { DialogComponent } from '@theme/dialog';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/* ═══════════════════════════════════════════════════════════════════════════
   CUSTOM VARIANT QUICK ADD DIALOG
   Desktop quick-add modal for collection pages.
   Extends Horizon's DialogComponent for scroll lock, animations, and
   keyboard/backdrop close handling.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLElement} contentContainer - The modal content container
 */

class CustomQuickAddDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #fetchAbortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #eventAbortController = new AbortController();

  /* ═══════════════════════════════════════════════════════════════════════
     SELECTORS & CONFIG
     ═══════════════════════════════════════════════════════════════════════ */
  static SELECTORS = {
    button: '.custom-variant-quick-add__button',
    card: '.custom-product-card',
    productGrid: '[data-product-grid-content]',
    variantInput: 'input[type="radio"][data-variant-id]',
    mediaColumn: '.product-information__media',
    detailsColumn: '.product-details',
    focusable:
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
  };

  static CLASSES = {
    loading: 'custom-variant-quick-add__button--loading',
  };

  /* ═══════════════════════════════════════════════════════════════════════
     LIFECYCLE
     ═══════════════════════════════════════════════════════════════════════ */

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#eventAbortController.signal,
    });

    document.addEventListener('click', this.#handleButtonClick, {
      signal: this.#eventAbortController.signal,
    });

    this.refs.dialog.addEventListener('keydown', this.#handleDialogTabKeyDown, {
      signal: this.#eventAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#fetchAbortController?.abort();
    this.#eventAbortController.abort();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     EVENT HANDLERS
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Delegated click handler for quick-add buttons across all product cards.
   * Eliminates need for per-button initialization and MutationObserver.
   * @param {MouseEvent} event
   */
  #handleButtonClick = async (event) => {
    const target = /** @type {Element} */ (event.target);
    const button = target?.closest(CustomQuickAddDialog.SELECTORS.button);
    if (!(button instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const card = button.closest(CustomQuickAddDialog.SELECTORS.card);
    if (!(card instanceof HTMLElement)) return;

    const link = card.querySelector('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const productUrl = new URL(link.href);
    const variantId = card.dataset.variantId;

    if (variantId) {
      productUrl.searchParams.set('variant', variantId);
    }

    try {
      button.classList.add(CustomQuickAddDialog.CLASSES.loading);
      await this.#showContent(productUrl.toString(), variantId);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[CustomQuickAddDialog]', error);
      }
    } finally {
      button.classList.remove(CustomQuickAddDialog.CLASSES.loading);
    }
  };

  /**
   * Closes modal and clears cache on successful cart add.
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    this.#cachedContent.clear();
    this.closeDialog();
  };

  /**
   * Keeps keyboard focus inside this custom modal using the same first/last
   * focusable pattern as Horizon's focus helper, filtered to rendered controls.
   * @param {KeyboardEvent} event
   */
  #handleDialogTabKeyDown = (event) => {
    if (event.key !== 'Tab') return;

    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const focusable = this.#getRenderedFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement;

    if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && (activeElement === first || activeElement === dialog)) {
      event.preventDefault();
      last.focus();
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════
     CONTENT MANAGEMENT
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Fetches product content, caches it, morphs into modal, and opens.
   * @param {string} url - Product page URL (with variant param if applicable)
   * @param {string} [variantId] - Variant ID to pre-select
   */
  async #showContent(url, variantId) {
    let productGrid = this.#cachedContent.get(url);

    if (!productGrid) {
      const doc = await this.#fetchProductPage(url);
      if (!doc) return;

      const gridElement = doc.querySelector(CustomQuickAddDialog.SELECTORS.productGrid);
      if (!gridElement) {
        throw new Error('Product grid content not found');
      }

      productGrid = /** @type {Element} */ (gridElement.cloneNode(true));

      // Extract product description from accordion (lives outside productGrid)
      // and inject as a simple div into product-details
      this.#injectDescription(doc, productGrid);

      this.#cachedContent.set(url, productGrid);
    }

    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    const contentContainer = /** @type {HTMLElement} */ (this.refs.contentContainer);
    morph(contentContainer, freshContent);

    this.showDialog();
    this.#resetModalScroll();

    if (variantId) {
      this.#preselectVariant(variantId);
    }
  }

  /**
   * Fetches product page HTML with abort support.
   * Cancels any in-flight request before starting a new one.
   * @param {string} url
   * @returns {Promise<Document|null>}
   */
  async #fetchProductPage(url) {
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.#fetchAbortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } finally {
      this.#fetchAbortController = null;
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     DOM OPERATIONS
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Extracts product description text from the fetched product page and injects
   * it as a simple div into product-details. The description lives in an accordion
   * outside [data-product-grid-content], so we pull it from the full doc.
   * @param {Document} doc - The full fetched product page document
   * @param {Element} productGrid - The cloned product grid element to inject into
   */
  #injectDescription(doc, productGrid) {
    // Find the accordion row with "Description" heading
    const accordionRows = doc.querySelectorAll('accordion-custom .details');
    let descriptionHTML = '';

    for (const row of accordionRows) {
      const summary = row.querySelector('.details__header');
      if (summary?.textContent?.trim().toLowerCase().includes('description')) {
        const content = row.querySelector('.details-content');
        if (content) {
          descriptionHTML = content.innerHTML.trim();
        }
        break;
      }
    }

    if (!descriptionHTML) return;

    const details = productGrid.querySelector(CustomQuickAddDialog.SELECTORS.detailsColumn);
    if (!details) return;

    // Find the buy button to insert before it
    const buyButtons = details.querySelector('.buy-buttons-block');
    if (!buyButtons) return;

    const descriptionEl = document.createElement('div');
    descriptionEl.className = 'custom-variant-quick-add__description rte';
    descriptionEl.innerHTML = descriptionHTML;

    buyButtons.parentNode?.insertBefore(descriptionEl, buyButtons);
  }

  /**
   * Resets scroll position on both modal columns.
   */
  #resetModalScroll() {
    requestAnimationFrame(() => {
      const contentContainer = /** @type {HTMLElement} */ (this.refs.contentContainer);
      const media = contentContainer.querySelector(CustomQuickAddDialog.SELECTORS.mediaColumn);
      const details = contentContainer.querySelector(CustomQuickAddDialog.SELECTORS.detailsColumn);
      if (media) media.scrollTop = 0;
      if (details) details.scrollTop = 0;
    });
  }

  /**
   * Pre-selects a variant by checking its radio input and dispatching change.
   * @param {string} variantId
   */
  #preselectVariant(variantId) {
    requestAnimationFrame(() => {
      const contentContainer = /** @type {HTMLElement} */ (this.refs.contentContainer);
      const inputs = contentContainer.querySelectorAll(
        CustomQuickAddDialog.SELECTORS.variantInput
      );
      if (!inputs) return;

      for (const input of inputs) {
        if (input instanceof HTMLInputElement && input.dataset.variantId === variantId) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          break;
        }
      }
    });
  }

  /**
   * Returns focusable elements that are actually participating in this modal's
   * rendered tab order. The injected product content contains hidden controls
   * that Horizon's generic trap can count but the browser will skip.
   * @returns {HTMLElement[]}
   */
  #getRenderedFocusableElements() {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement)) return [];

    return Array.from(dialog.querySelectorAll(CustomQuickAddDialog.SELECTORS.focusable)).filter(
      (element) => element instanceof HTMLElement && this.#isRenderedFocusable(element)
    );
  }

  /**
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #isRenderedFocusable(element) {
    if (element.closest('[aria-hidden="true"], [inert]')) return false;
    if (!element.getClientRects().length) return false;

    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }
}

if (!customElements.get('custom-quick-add-dialog')) {
  customElements.define('custom-quick-add-dialog', CustomQuickAddDialog);
}
